import React from 'react';
import InfoIcon from '@mui/icons-material/Info';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Grid, Typography, useTheme } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { di } from '../../../state/inversify.config';
import { getRouteByAccountType } from '../../../routes/getRouteByAccountType';
import { IncidentListDomainService } from '../../../pages/incidentsList/incidentListDomainService';
import { currentLocaleSports } from '../../../locale/LocaleConfig';
import { useCustomTranslation } from '../../../translation/customTranslation';
import {
  createLocalisation,
  LocalisationProps,
} from '../../../translation/helpers/enumLocalisation';
import {
  MechanismOfInjury,
  PlayType,
  SessionType,
  SportType,
} from '../../../pages/createIncident/smart/completeIncident.enum';
import { Button } from '../../../components/button/button';

export const OpenIncidentsList = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = useTheme();
  const { t: tCustom } = useCustomTranslation();

  const sessionTypeList: LocalisationProps[] = Object.values(
    createLocalisation(SessionType, 'incident.sessionType'),
  );

  const playTypeList: LocalisationProps[] = Object.values(
    createLocalisation(PlayType, 'incident.playType'),
  );
  const headImpactTypeList: LocalisationProps[] = Object.values(
    createLocalisation(MechanismOfInjury, 'incident.mechanismOfInjury'),
  );

  const incidentListDomainService = di.get<IncidentListDomainService>(
    IncidentListDomainService,
  );
  const openIncidents = incidentListDomainService.useOpenIncidentSelector();
  const user = incidentListDomainService.useUserSelector();

  const navigateViewIncident = (playerId: string, incidentId: string) => {
    navigate(
      `/${getRouteByAccountType(
        user?.accountType,
      )}/view-incident?selectedPlayerId=${playerId}&selectedIncidentId=${incidentId}`,
    );
  };

  return (
    <>
      {openIncidents?.length <= 0 && (
        <Typography variant="h6">
          {t('incident.INCIDENT_LIST_YOUR_KIDS_ARE_CLEAR')}
        </Typography>
      )}
      {openIncidents?.length === 1 && (
        <Typography variant="h6">
          {t('incident.INCIDENT_LIST_YOU_HAVE_INCIDENTS_SINGULAR')}
        </Typography>
      )}
      {openIncidents?.length >= 2 && (
        <Typography variant="h6">
          {t('incident.INCIDENT_LIST_YOU_HAVE_INCIDENTS_PLURAL', {
            incidentCount: openIncidents?.length,
          })}
        </Typography>
      )}

      {openIncidents?.map((incident) => {
        const activityLocalised = tCustom(
          currentLocaleSports.find((act) => act.value === incident.activity)
            ?.i18nKey || '',
        );
        const sessionTypeLocalised = tCustom(
          sessionTypeList.find((act) => act.value === incident.sessionType)
            ?.i18nKey || '',
        );
        const playTypeLocalised = tCustom(
          playTypeList.find((act) => act.value === incident.playType)
            ?.i18nKey || '',
        );
        const headImpactTypeLocalised = tCustom(
          headImpactTypeList.find(
            (act) => act.value === incident.headImpactType,
          )?.i18nKey || '',
        );

        return (
          <Grid
            sx={{
              p: 2,
              background: theme.palette.primary.main,
              color: theme.palette.text.secondary,
              borderRadius: 4,
              mt: 1,
              mb: 1,
              maxWidth: 550,
            }}
            key={incident.id}
          >
            <Grid container justifyContent="space-between">
              <Grid item sx={{ my: 1 }}>
                <Typography variant="h6" fontWeight="600">
                  {t('incident.INCIDENT_LIST_OPEN_INCIDENTS')}
                </Typography>
              </Grid>
              <Grid item sx={{ my: 1 }}>
                <InfoIcon sx={{ fill: theme.palette.error.main }} />
              </Grid>
            </Grid>
            <Grid container justifyContent="space-between">
              <Grid item sx={{ my: 1 }}>
                <Typography variant="subtitle1" fontWeight="600">
                  {t('incident.INCIDENT_LIST_CHILD')}
                </Typography>
              </Grid>
              <Grid item sx={{ my: 1 }}>
                <Typography
                  variant="subtitle1"
                  fontWeight="600"
                  color={theme.palette.text.secondary}
                  data-testid="openIncidentPlayerName"
                >
                  {incident.playerName}
                </Typography>
              </Grid>
            </Grid>
            <Grid container justifyContent="space-between">
              <Grid item sx={{ my: 1 }}>
                <Typography
                  variant="subtitle1"
                  fontWeight="600"
                  color={theme.palette.text.secondary}
                >
                  {t('incident.INCIDENT_EMERGENCY')}
                </Typography>
              </Grid>
              <Grid item sx={{ my: 1 }}>
                <Typography
                  variant="subtitle1"
                  fontWeight="600"
                  color={theme.palette.text.secondary}
                >
                  {incident.redFlags
                    ? t('common.generic.GENERIC_CONFIRMATION_AFFIRMATIVE')
                    : t('common.generic.GENERIC_CONFIRMATION_NEGATIVE')}
                </Typography>
              </Grid>
            </Grid>
            <Grid container justifyContent="space-between">
              <Grid item sx={{ my: 1 }}>
                <Typography
                  variant="subtitle1"
                  fontWeight="600"
                  color={theme.palette.text.secondary}
                >
                  {t('incident.sport.ACTIVITY_TITLE')}
                </Typography>
              </Grid>
              <Grid item sx={{ my: 1 }}>
                <Typography
                  variant="subtitle1"
                  fontWeight="600"
                  color={theme.palette.text.secondary}
                >
                  {incident.activity &&
                  incident.activity === SportType.OTHER &&
                  incident.activityOther
                    ? incident.activityOther
                    : activityLocalised}
                </Typography>
              </Grid>
            </Grid>
            {incident.sessionType && (
              <Grid container justifyContent="space-between">
                <Grid item sx={{ my: 1 }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight="600"
                    color={theme.palette.text.secondary}
                  >
                    {t('incident.sessionType.SESSION_TYPE_TITLE')}
                  </Typography>
                </Grid>
                <Grid item sx={{ my: 1 }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight="600"
                    color={theme.palette.text.secondary}
                  >
                    {incident.sessionType &&
                    incident.sessionType === SessionType.OTHER &&
                    incident.sessionTypeOther
                      ? incident.sessionTypeOther
                      : sessionTypeLocalised}
                  </Typography>
                </Grid>
              </Grid>
            )}
            {incident.playType && (
              <Grid container justifyContent="space-between">
                <Grid item sx={{ my: 1 }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight="600"
                    color={theme.palette.text.secondary}
                  >
                    {t('incident.playType.PLAY_TYPE_TITLE')}
                  </Typography>
                </Grid>
                <Grid item sx={{ my: 1 }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight="600"
                    color={theme.palette.text.secondary}
                  >
                    {incident.playType &&
                    incident.playType === PlayType.OTHER &&
                    incident.playTypeOther
                      ? incident.playTypeOther
                      : playTypeLocalised}
                  </Typography>
                </Grid>
              </Grid>
            )}
            {incident.headImpactType && (
              <Grid container justifyContent="space-between">
                <Grid item sx={{ my: 1 }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight="600"
                    color={theme.palette.text.secondary}
                  >
                    {t('incident.mechanismOfInjury.HEAD_IMPACT_TYPE_TITLE')}
                  </Typography>
                </Grid>
                <Grid item sx={{ my: 1 }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight="600"
                    color={theme.palette.text.secondary}
                  >
                    {incident.headImpactType &&
                    incident.headImpactType === MechanismOfInjury.OTHER &&
                    incident.headImpactTypeOther
                      ? incident.headImpactTypeOther
                      : headImpactTypeLocalised}
                  </Typography>
                </Grid>
              </Grid>
            )}
            <Grid container justifyContent="space-between">
              <Grid item sx={{ my: 1 }}>
                <Typography
                  variant="subtitle1"
                  fontWeight="600"
                  color={theme.palette.text.secondary}
                >
                  {t('incident.INCIDENT_LIST_WHATS_NEXT')}
                </Typography>
              </Grid>
            </Grid>
            <Grid container justifyContent="center">
              <Grid item sx={{ my: 1 }}>
                <Button
                  type="submit"
                  color="secondary"
                  fullWidth
                  variant="contained"
                  onClick={() =>
                    navigateViewIncident(incident.playerId, incident.id)
                  }
                  startIcon={<ChevronRightIcon />}
                  label={t('incident.REVIEW_INCIDENTS_AND_OTHER_REVIEWS')}
                />
              </Grid>
            </Grid>
          </Grid>
        );
      })}
    </>
  );
};
